import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMsal } from '@azure/msal-react';
import PageHeader from '../common/PageHeader';
import { createApiKey, listApiKeys, revokeApiKey } from '../../helper/apiKeysApi';

const GUELTIGKEIT = [
  { value: 30, label: '30 Tage' },
  { value: 90, label: '90 Tage' },
  { value: 365, label: '1 Jahr' },
  { value: 'never', label: 'Unbegrenzt' },
];

const STATUS = {
  active: { label: 'aktiv', color: 'success' },
  expired: { label: 'abgelaufen', color: 'default' },
  revoked: { label: 'widerrufen', color: 'default' },
};

const LEERES_FORMULAR = { name: '', mode: 'app', expires: 365 };

function datum(iso) {
  return iso
    ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
}

export default function ApiKeysView() {
  const { instance } = useMsal();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(LEERES_FORMULAR);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [neuerSchluessel, setNeuerSchluessel] = useState(null);
  const [kopiert, setKopiert] = useState(false);
  const [widerrufen, setWiderrufen] = useState(null);

  const laden = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setKeys(await listApiKeys(instance));
    } catch (e) {
      setError(e.message || 'Schlüssel konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => {
    laden();
  }, [laden]);

  const formularOeffnen = () => {
    setFormError(null);
    setFormOpen(true);
  };

  const erzeugen = async () => {
    setSaving(true);
    setFormError(null);
    try {
      const ergebnis = await createApiKey(instance, {
        name: form.name.trim(),
        mode: form.mode,
        expires_in_days: form.expires === 'never' ? null : form.expires,
      });
      setFormOpen(false);
      setForm(LEERES_FORMULAR);
      setKopiert(false);
      setNeuerSchluessel(ergebnis);
      laden();
    } catch (e) {
      // Das Backend nennt den genauen Grund, z. B. das erlaubte Namensformat.
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const kopieren = async () => {
    try {
      await navigator.clipboard.writeText(neuerSchluessel.key);
      setKopiert(true);
    } catch {
      setKopiert(false);
    }
  };

  const widerrufBestaetigen = async () => {
    const ziel = widerrufen;
    setWiderrufen(null);
    try {
      await revokeApiKey(instance, ziel.id);
      laden();
    } catch (e) {
      // Nicht neu laden: laden() setzt den Fehler zurueck, die Meldung waere sofort weg.
      setError(e.message || 'Schlüssel konnte nicht widerrufen werden.');
    }
  };

  const basis = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  return (
    <Box sx={{ maxWidth: 1120, mx: 'auto', p: { xs: 2, md: 4 }, textAlign: 'left' }}>
      <PageHeader
        overline="Admin"
        title="API-Schlüssel"
        subtitle="Schlüssel für Anwendungen, die Transcribe ohne Browser nutzen. Ein Schlüssel kann nur transkribieren – keine KI-Funktionen und keine Verwaltung."
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={formularOeffnen}>
            Neuer Schlüssel
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Handelt als</TableCell>
              <TableCell>Schlüssel</TableCell>
              <TableCell>Erzeugt</TableCell>
              <TableCell>Gültig bis</TableCell>
              <TableCell>Zuletzt benutzt</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>Noch keine Schlüssel.</TableCell>
              </TableRow>
            )}
            {keys.map((k) => (
              <TableRow key={k.id}>
                <TableCell>{k.name}</TableCell>
                <TableCell>{k.identity}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{k.key_prefix}…</TableCell>
                <TableCell>
                  {datum(k.created_at)}
                  <Typography variant="caption" color="text.secondary" display="block">
                    {k.created_by}
                  </Typography>
                </TableCell>
                <TableCell>{k.expires_at ? datum(k.expires_at) : 'unbegrenzt'}</TableCell>
                <TableCell>{datum(k.last_used_at)}</TableCell>
                <TableCell>
                  <Chip size="small" label={STATUS[k.status].label} color={STATUS[k.status].color} />
                </TableCell>
                <TableCell align="right">
                  {k.status === 'active' && (
                    <Button size="small" color="error" onClick={() => setWiderrufen(k)}>
                      Widerrufen
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper variant="outlined" sx={{ mt: 3, p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Nutzung
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Den Schlüssel als Header mitschicken. Hochladen liefert eine <code>job_id</code>; damit den
          Status abfragen, bis er <code>completed</code> ist, dann das Ergebnis abholen. Höchstens drei
          Aufträge dürfen gleichzeitig offen sein. Die Liste enthält keinen Text; mit{' '}
          <code>limit</code> kommt sie seitenweise, weiter mit dem gelieferten <code>next_cursor</code>.
        </Typography>
        <Box
          component="pre"
          sx={{ fontSize: 12, overflowX: 'auto', m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}
        >
          {`curl -H "Authorization: Bearer $TRANSCRIBE_KEY" -F "file=@aufnahme.mp3" -F "language=de" ${basis}/transcribe
curl -H "Authorization: Bearer $TRANSCRIBE_KEY" ${basis}/job_status/<job_id>
curl -H "Authorization: Bearer $TRANSCRIBE_KEY" ${basis}/transcription_result/<job_id>
curl -H "Authorization: Bearer $TRANSCRIBE_KEY" "${basis}/transcriptions?limit=10"`}
        </Box>
      </Paper>

      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Neuer API-Schlüssel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl>
              <FormLabel>Handelt als</FormLabel>
              <RadioGroup value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <FormControlLabel
                  value="app"
                  control={<Radio size="small" />}
                  label="Eigene Anwendung (getrennt von deinen Transkripten)"
                />
                <FormControlLabel
                  value="self"
                  control={<Radio size="small" />}
                  label="Für mich (Transkripte erscheinen in deinem Konto)"
                />
              </RadioGroup>
            </FormControl>
            <TextField
              label="Name"
              size="small"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              helperText={
                form.mode === 'app'
                  ? 'Kleinbuchstaben, Ziffern, Bindestrich – z. B. vravatar'
                  : 'Wofür du den Schlüssel nutzt, z. B. marvin-cli'
              }
              autoFocus
            />
            <FormControl size="small">
              <InputLabel id="gueltigkeit-label">Gültig für</InputLabel>
              <Select
                labelId="gueltigkeit-label"
                label="Gültig für"
                value={form.expires}
                onChange={(e) => setForm({ ...form, expires: e.target.value })}
              >
                {GUELTIGKEIT.map((g) => (
                  <MenuItem key={g.value} value={g.value}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={erzeugen} disabled={saving || !form.name.trim()}>
            Erzeugen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ohne onClose: Der Schluessel ist nur hier sichtbar, ein Klick daneben soll ihn nicht verwerfen. */}
      <Dialog open={Boolean(neuerSchluessel)} maxWidth="sm" fullWidth>
        <DialogTitle>Schlüssel erzeugt</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Kopiere den Schlüssel jetzt. Er wird nur dieses eine Mal angezeigt und lässt sich später
            nicht wiederherstellen.
          </Alert>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              value={neuerSchluessel?.key || ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace' } }}
            />
            <Tooltip title={kopiert ? 'Kopiert' : 'Kopieren'}>
              <IconButton onClick={kopieren} aria-label="Schlüssel kopieren">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Handelt als <strong>{neuerSchluessel?.identity}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setNeuerSchluessel(null)}>
            Fertig
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(widerrufen)} onClose={() => setWiderrufen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Schlüssel widerrufen?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            „{widerrufen?.name}" ({widerrufen?.key_prefix}…) wird sofort gesperrt. Anwendungen, die ihn
            nutzen, bekommen ab dann keinen Zugriff mehr. Bereits erzeugte Transkripte bleiben erhalten.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWiderrufen(null)}>Abbrechen</Button>
          <Button color="error" variant="contained" onClick={widerrufBestaetigen}>
            Widerrufen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
